// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyPicture from "@awc/MobiusOverrides/LazyImage/LazyPicture";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import { xParserOptions } from "@awc/XDL/Utilities";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import styled from "styled-components";

const enum fields {
    desktopImagePath = "desktopImagePath",
    mobileImagePath = "mobileImagePath",
    title = "title",
    description = "description",
    link = "link",
    openInNewTab = "openInNewTab",
}

interface InfoCardItemProps extends WidgetProps {
    fields: {
        [fields.desktopImagePath]: string;
        [fields.mobileImagePath]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.link]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
    };
}

interface InfoCardStyles {
    container?: InjectableCss;
    title?: LinkPresentationProps;
    description?: TypographyProps;
    image?: LazyImageProps;
    clickableImage?: ClickablePresentationProps;
}

export const infoCardStyles: InfoCardStyles = {};

const styles = infoCardStyles;

const Article = getStyledWrapper("article");

const InfoCard: React.FC<InfoCardItemProps> = ({ fields }) => {
    const url = useGetLink(fields.link).url;

    const image = fields.mobileImagePath ? (
        <LazyPicture
            smallImageSrc={fields.mobileImagePath || fields.desktopImagePath}
            mediumImageSrc={fields.desktopImagePath}
            largeImageSrc={fields.desktopImagePath}
            altText={fields.title}
            {...styles.image}
        />
    ) : (
        <LazyImage src={fields.desktopImagePath} altText={fields.title} {...styles.image} />
    );

    return (
        <Article {...styles.container} data-test-selector="InfoCard">
            {url ? (
                <>
                    <Clickable
                        {...styles.clickableImage}
                        href={url}
                        target={fields.openInNewTab ? "_blank" : undefined}
                        aria-label={fields.title}
                    >
                        {image}
                    </Clickable>
                    {fields.title && url && (
                        <Link
                            aria-label={fields.title}
                            href={url}
                            {...styles.title}
                            target={fields.openInNewTab ? "_blank" : undefined}
                        >
                            {fields.title}
                        </Link>
                    )}
                </>
            ) : (
                <>
                    {image}
                    {fields.title && <Typography {...styles.title?.typographyProps}>{fields.title}</Typography>}
                </>
            )}
            {fields.description && (
                <Typography {...styles.description}>{parse(fields.description, xParserOptions)}</Typography>
            )}
        </Article>
    );
};

const widgetModule: WidgetModule = {
    component: InfoCard,
    definition: {
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: fields.desktopImagePath,
                displayName: "Desktop Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
                fieldType: "General",
            },
            {
                name: fields.mobileImagePath,
                displayName: "Mobile Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.link,
                editorTemplate: "LinkField",
                defaultValue: { value: "", type: "Url" },
                fieldType: "General",
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                variant: "toggle",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
